/* eslint-disable max-lines */
/**
 * VoyagePDFContent.js
 *
 * FT-195: React component that renders voyage data for PDF export
 * Uses the same card components as VoyageLogView for pixel-perfect output
 *
 * Rendered off-screen, then converted to PDF via html2pdf.js
 */

import React from 'react';
import { Anchor, Flag, Check, X, CloudSun, Users, Fuel, UserPlus, Wind, Waves, Eye, Activity, ClipboardCheck } from 'lucide-react';
import { portLabel } from '../../../../utils/voyage/portLabel';

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

const formatTime = (timestamp) => {
  if (!timestamp) return '';
  try {
    return new Date(timestamp).toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return '';
  }
};

const formatDate = (timestamp) => {
  if (!timestamp) return '';
  try {
    return new Date(timestamp).toLocaleDateString('en-GB', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  } catch {
    return '';
  }
};

const formatDateTime = (timestamp) => {
  if (!timestamp) return '';
  return `${formatDate(timestamp)} ${formatTime(timestamp)}`;
};

const capitalize = (str) => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

// ============================================================================
// PROCESS UNIFIED LOG (matches VoyageLogView.js logic)
// ============================================================================

const processUnifiedLog = (unifiedLog, voyage) => {
  if (!unifiedLog || unifiedLog.length === 0) return [];

  // Build operation groups
  const operationGroups = {};
  unifiedLog.forEach(entry => {
    if (entry.operationId && ['section', 'operation_data', 'check', 'operation_complete'].includes(entry.type)) {
      if (entry.operationType === 'pre_departure' || entry.checkType === 'pre_departure') return;

      if (!operationGroups[entry.operationId]) {
        operationGroups[entry.operationId] = { section: null, data: null, checks: [], complete: null };
      }

      const group = operationGroups[entry.operationId];
      if (entry.type === 'section') group.section = entry;
      if (entry.type === 'operation_data') group.data = entry;
      if (entry.type === 'check') group.checks.push(entry);
      if (entry.type === 'operation_complete') group.complete = entry;
    }
  });

  // Create processed entries
  let entries = [...unifiedLog];

  // Find pre-departure checks
  const preDepartureChecks = entries.filter(e => e.type === 'check' && e.checkType === 'pre_departure');
  const letGoIndex = entries.findIndex(e => e.type === 'milestone' && e.milestoneType === 'let_go');

  if (preDepartureChecks.length > 0 && letGoIndex !== -1) {
    entries = entries.filter(e => !(e.type === 'check' && e.checkType === 'pre_departure'));
    entries.unshift({
      id: 'pre_departure_grouped',
      type: 'pre_departure_card',
      timestamp: preDepartureChecks[0]?.timestamp || voyage.departureTime,
      checks: preDepartureChecks
    });
  }

  // Process entries
  const result = [];
  const processedOperationIds = new Set();

  entries.forEach(entry => {
    if (entry.operationId && operationGroups[entry.operationId]) {
      const group = operationGroups[entry.operationId];
      if (entry.type === 'section' && !processedOperationIds.has(entry.operationId)) {
        processedOperationIds.add(entry.operationId);
        result.push({
          id: `operation_card_${entry.operationId}`,
          type: 'operation_card',
          operationId: entry.operationId,
          operationType: entry.operationType,
          timestamp: entry.timestamp,
          sectionEntry: group.section,
          operationDataEntry: group.data,
          checks: group.checks,
          completeEntry: group.complete
        });
      }
      return;
    }

    if (['section', 'operation_data', 'operation_complete'].includes(entry.type)) return;
    if (entry.type === 'check' && entry.operationId && operationGroups[entry.operationId]) return;

    result.push(entry);
  });

  return result;
};

// ============================================================================
// CARD COMPONENTS (PDF-optimized versions)
// ============================================================================

// Header Card
const PDFHeader = ({ voyage, vesselName }) => {
  const fromName = portLabel(voyage.from, 'Unknown');
  const toName = portLabel(voyage.to, 'Unknown');
  const voyageNumber = voyage.voyageNumber || voyage.number || '-';

  return (
    <div className="pdf-card" style={{ marginBottom: '6px', padding: '12px', backgroundColor: '#1e293b', color: 'white', borderRadius: '8px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1 style={{ fontSize: '18px', fontWeight: 'bold', margin: 0 }}>{vesselName}</h1>
          <p style={{ fontSize: '14px', color: '#94a3b8', marginTop: '4px' }}>Voyage #{voyageNumber}</p>
        </div>
        <div style={{ textAlign: 'right', fontSize: '12px', color: '#94a3b8' }}>
          <p style={{ margin: 0 }}>Generated: {formatDateTime(new Date())}</p>
        </div>
      </div>
      <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #334155' }}>
        <p style={{ fontSize: '14px', margin: 0 }}>
          <strong>{fromName}</strong> → <strong>{toName}</strong>
        </p>
        <p style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px' }}>
          Departed: {formatDateTime(voyage.departureTime)}
          {voyage.arrivalTime && <span> | Arrived: {formatDateTime(voyage.arrivalTime)}</span>}
        </p>
      </div>
    </div>
  );
};

// Pre-Departure Card
const PDFPreDepartureCard = ({ voyage, checks }) => {
  const fromName = portLabel(voyage.from, 'Unknown');
  const toName = portLabel(voyage.to, 'Unknown');
  const crewCount = voyage.crewCount || voyage.crew?.length || 0;
  const passengerCount = voyage.passengerCount || 0;
  const fuelLevel = voyage.departureFuel;
  const weather = voyage.departureWeather || {};

  const preDepartureChecks = checks?.filter(c => c.checkType === 'pre_departure') || [];
  const completedCount = preDepartureChecks.filter(c => c.completed).length;

  return (
    <div className="pdf-card" style={{ marginBottom: '4px', padding: '10px', backgroundColor: 'white', borderRadius: '8px', borderLeft: '4px solid #0891b2' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
        <ClipboardCheck style={{ width: '16px', height: '16px', color: '#0891b2' }} />
        <span style={{ fontSize: '14px', fontWeight: '600' }}>Pre-Departure</span>
      </div>

      <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>{fromName} → {toName}</p>

      <div style={{ display: 'flex', gap: '16px', fontSize: '12px', marginBottom: '8px' }}>
        <span><Users style={{ width: '12px', height: '12px', display: 'inline', marginRight: '4px' }} />Crew: {crewCount} POB</span>
        {fuelLevel !== undefined && <span><Fuel style={{ width: '12px', height: '12px', display: 'inline', marginRight: '4px' }} />Fuel: {fuelLevel}%</span>}
        <span><UserPlus style={{ width: '12px', height: '12px', display: 'inline', marginRight: '4px' }} />Pax: {passengerCount}</span>
      </div>

      {(weather.windDirection || weather.seaState || weather.visibility) && (
        <div style={{ display: 'flex', gap: '12px', fontSize: '11px', color: '#6b7280', marginBottom: '8px' }}>
          {weather.windDirection && <span><Wind style={{ width: '11px', height: '11px', display: 'inline', marginRight: '2px' }} />{weather.windDirection} {weather.windStrength || weather.windSpeed || ''}</span>}
          {weather.seaState && <span><Waves style={{ width: '11px', height: '11px', display: 'inline', marginRight: '2px' }} />{weather.seaState}</span>}
          {weather.visibility && <span><Eye style={{ width: '11px', height: '11px', display: 'inline', marginRight: '2px' }} />{weather.visibility}</span>}
        </div>
      )}

      {preDepartureChecks.length > 0 && (
        <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '8px', marginTop: '8px' }}>
          <p style={{ fontSize: '12px', fontWeight: '500', marginBottom: '6px' }}>Pre-Departure Checks ({completedCount}/{preDepartureChecks.length})</p>
          {preDepartureChecks.map((check, idx) => (
            <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', marginBottom: '3px' }}>
              {check.completed ? (
                <Check style={{ width: '12px', height: '12px', color: '#22c55e' }} />
              ) : (
                <div style={{ width: '12px', height: '12px', border: '2px solid #d1d5db', borderRadius: '50%' }} />
              )}
              <span style={{ color: check.completed ? '#374151' : '#9ca3af' }}>{check.text || check.label || check.name}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Milestone Banner (LET GO / ALL FAST)
const PDFMilestoneBanner = ({ entry }) => {
  const isLetGo = entry.milestoneType === 'let_go';
  const label = isLetGo ? 'LET GO' : 'ALL FAST';
  const Icon = isLetGo ? Anchor : Flag;

  return (
    <div className="pdf-card" style={{ marginBottom: '4px', padding: '8px 12px', backgroundColor: '#1e293b', color: 'white', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '10px' }}>
      <Icon style={{ width: '16px', height: '16px' }} />
      <span style={{ fontWeight: 'bold', fontSize: '13px' }}>{label}</span>
      {entry.location && <span style={{ color: '#94a3b8' }}>— {entry.location}</span>}
      <span style={{ marginLeft: 'auto', fontSize: '11px', color: '#64748b', fontFamily: 'monospace' }}>{formatTime(entry.timestamp)}</span>
    </div>
  );
};

// Weather Report Card
const PDFWeatherCard = ({ entry }) => {
  const windDirection = entry.data?.windDirection || entry.windDirection;
  const windSpeed = entry.data?.windSpeed || entry.windSpeed;
  const seaState = entry.data?.seaState || entry.seaState;
  const visibility = entry.data?.visibility || entry.visibility;
  const notes = entry.data?.notes || entry.notes;
  const recorder = entry.recordedBy || entry.createdBy?.name || 'Unknown';

  const formatWind = () => {
    if (!windDirection && !windSpeed) return '---';
    const parts = [];
    if (windDirection) parts.push(windDirection);
    if (windSpeed !== null && windSpeed !== undefined) {
      parts.push(typeof windSpeed === 'number' ? `${windSpeed}kt` : windSpeed);
    }
    return parts.join(' ') || '---';
  };

  return (
    <div className="pdf-card" style={{ marginBottom: '4px', padding: '10px', backgroundColor: 'white', borderRadius: '8px', borderLeft: '4px solid #f59e0b' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <CloudSun style={{ width: '16px', height: '16px', color: '#f59e0b' }} />
          <span style={{ fontSize: '13px', fontWeight: '600' }}>Weather Report</span>
        </div>
        <span style={{ fontSize: '11px', color: '#9ca3af', fontFamily: 'monospace' }}>{formatTime(entry.timestamp)}</span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '8px' }}>
        <div>
          <p style={{ fontSize: '10px', color: '#6b7280', textTransform: 'uppercase', marginBottom: '2px' }}>Wind</p>
          <p style={{ fontSize: '12px', fontWeight: '500' }}>{formatWind()}</p>
        </div>
        <div>
          <p style={{ fontSize: '10px', color: '#6b7280', textTransform: 'uppercase', marginBottom: '2px' }}>Sea State</p>
          <p style={{ fontSize: '12px', fontWeight: '500' }}>{capitalize(seaState) || '---'}</p>
        </div>
        <div>
          <p style={{ fontSize: '10px', color: '#6b7280', textTransform: 'uppercase', marginBottom: '2px' }}>Visibility</p>
          <p style={{ fontSize: '12px', fontWeight: '500' }}>{capitalize(visibility) || '---'}</p>
        </div>
      </div>

      {notes && (
        <p style={{ fontSize: '11px', color: '#6b7280', fontStyle: 'italic', borderTop: '1px solid #f3f4f6', paddingTop: '6px', marginTop: '6px' }}>{notes}</p>
      )}

      <p style={{ fontSize: '10px', color: '#9ca3af', marginTop: '6px' }}>Recorded by: {recorder}</p>
    </div>
  );
};

// Operation Card
const PDFOperationCard = ({ entry }) => {
  const { operationType, sectionEntry, operationDataEntry, checks = [], completeEntry } = entry;
  const opName = (operationType || 'operation').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  const isComplete = !!completeEntry;

  // Calculate duration
  let durationText = '-';
  if (sectionEntry?.timestamp) {
    const startMs = new Date(sectionEntry.timestamp).getTime();
    const endMs = completeEntry?.timestamp ? new Date(completeEntry.timestamp).getTime() : Date.now();
    const durationMins = Math.round((endMs - startMs) / 60000);
    if (durationMins < 60) {
      durationText = `${durationMins} min`;
    } else {
      const hours = Math.floor(durationMins / 60);
      const mins = durationMins % 60;
      durationText = mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
    }
  }

  // Extract custom fields
  const standardFields = ['id', 'type', 'operationId', 'operationType', 'vesselId', 'voyageId', 'timestamp', 'createdAt', 'updatedAt', 'syncedAt', 'completedAt', 'createdBy', 'updatedBy', 'completedBy', 'locked', 'synced', 'syncId', 'syncStatus', 'localId', 'serverId', 'version', '_rev', '_id'];
  const customFields = operationDataEntry
    ? Object.entries(operationDataEntry).filter(([k, v]) => !standardFields.includes(k) && v !== null && v !== undefined && v !== '').slice(0, 4)
    : [];

  const startedBy = sectionEntry?.createdBy?.name || sectionEntry?.createdBy || 'Unknown';

  return (
    <div className="pdf-card" style={{ marginBottom: '4px', padding: '10px', backgroundColor: 'white', borderRadius: '8px', borderLeft: '4px solid #0d9488' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <Activity style={{ width: '16px', height: '16px', color: '#0d9488' }} />
          <span style={{ fontSize: '13px', fontWeight: '600' }}>{opName}</span>
        </div>
        <span style={{ fontSize: '11px', color: '#9ca3af', fontFamily: 'monospace' }}>{formatTime(sectionEntry?.timestamp)}</span>
      </div>

      {customFields.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: customFields.length === 1 ? '1fr' : '1fr 1fr', gap: '8px', marginBottom: '8px' }}>
          {customFields.map(([key, value]) => (
            <div key={key}>
              <p style={{ fontSize: '10px', color: '#6b7280', textTransform: 'uppercase', marginBottom: '2px' }}>{key.replace(/_/g, ' ')}</p>
              <p style={{ fontSize: '12px', fontWeight: '500' }}>{String(value)}</p>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', padding: '8px 0', borderTop: '1px solid #f3f4f6' }}>
        <div>
          <p style={{ fontSize: '10px', color: '#6b7280', textTransform: 'uppercase', marginBottom: '2px' }}>Duration</p>
          <p style={{ fontSize: '12px', fontWeight: '500' }}>{durationText}</p>
        </div>
        <div>
          <p style={{ fontSize: '10px', color: '#6b7280', textTransform: 'uppercase', marginBottom: '2px' }}>Status</p>
          <p style={{ fontSize: '12px', fontWeight: '500', color: isComplete ? '#16a34a' : '#d97706' }}>
            {isComplete ? '✓ Complete' : '◉ In Progress'}
          </p>
        </div>
      </div>

      {checks.length > 0 && (
        <div style={{ borderTop: '1px solid #f3f4f6', paddingTop: '8px', marginTop: '4px' }}>
          <p style={{ fontSize: '11px', fontWeight: '500', marginBottom: '6px' }}>
            Checklist ({checks.filter(c => c.completed || c.skipped).length}/{checks.length})
          </p>
          {checks.map((check, idx) => (
            <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', marginBottom: '3px' }}>
              {check.completed ? (
                <Check style={{ width: '12px', height: '12px', color: '#22c55e' }} />
              ) : check.skipped ? (
                <X style={{ width: '12px', height: '12px', color: '#f59e0b' }} />
              ) : (
                <div style={{ width: '12px', height: '12px', border: '2px solid #d1d5db', borderRadius: '4px' }} />
              )}
              <span style={{ color: check.completed ? '#374151' : check.skipped ? '#d97706' : '#9ca3af', textDecoration: check.skipped ? 'line-through' : 'none' }}>
                {check.text || check.label}
              </span>
              {(check.completed || check.skipped) && check.completedAt && (
                <span style={{ marginLeft: 'auto', fontSize: '10px', color: '#9ca3af' }}>{formatTime(check.completedAt)}</span>
              )}
            </div>
          ))}
        </div>
      )}

      <p style={{ fontSize: '10px', color: '#9ca3af', marginTop: '8px', paddingTop: '6px', borderTop: '1px solid #f3f4f6' }}>
        {isComplete ? 'Completed' : 'Started'} by: {startedBy}
      </p>
    </div>
  );
};

// Simple Entry Card (note, position, fuel, comms, pilot)
const PDFSimpleEntry = ({ entry }) => {
  const typeLabel = entry.type?.replace(/_/g, ' ').toUpperCase() || 'ENTRY';

  let details = '';
  if (entry.type === 'note') {
    details = entry.text || entry.note || entry.details || '';
  } else if (entry.type === 'position') {
    const lat = entry.latitude || entry.lat;
    const lon = entry.longitude || entry.lon || entry.lng;
    details = lat && lon ? `${lat}, ${lon}` : entry.position || '';
  } else if (entry.type === 'fuel') {
    details = entry.level !== undefined ? `${entry.level}%` : entry.details || '';
  } else if (entry.type === 'comms') {
    details = entry.message || entry.details || '';
  } else if (entry.type === 'pilot') {
    details = entry.action || entry.details || '';
  } else {
    details = entry.details || entry.text || entry.note || '';
  }

  const recorder = entry.recordedBy || entry.createdBy?.name || entry.createdBy || '';

  return (
    <div className="pdf-card" style={{ marginBottom: '3px', padding: '6px 10px', backgroundColor: 'white', borderRadius: '6px', fontSize: '11px', display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
      <span style={{ color: '#6b7280', fontWeight: '500', flexShrink: 0 }}>{typeLabel}</span>
      <span style={{ color: '#374151', flex: 1 }}>{details}</span>
      {recorder && <span style={{ color: '#9ca3af', fontSize: '10px', flexShrink: 0 }}>— {recorder}</span>}
      <span style={{ color: '#9ca3af', fontFamily: 'monospace', flexShrink: 0, marginLeft: 'auto' }}>{formatTime(entry.timestamp)}</span>
    </div>
  );
};

// Sign-Off Block
// FT-202/196: Handle all sign-off formats:
// 1. confirmedBy (string) - from ConfirmSignOffModal
// 2. signedBy (string) - old drawn signatures
// 3. signedBy (object with .name) - broken format, read .name from it
const PDFSignOff = ({ voyage }) => {
  const signOff = voyage.signOff;
  if (!signOff) return null;

  // Safe name extraction handling all formats
  const getSignedByName = () => {
    if (signOff.confirmedBy) return signOff.confirmedBy;
    if (signOff.signedBy?.name) return signOff.signedBy.name;
    if (typeof signOff.signedBy === 'string') return signOff.signedBy;
    if (signOff.masterName) return signOff.masterName;
    return 'Unknown';
  };

  // Safe role extraction
  const getRole = () => {
    if (signOff.role) return signOff.role;
    if (signOff.signedBy?.role) return signOff.signedBy.role;
    return null;
  };

  // Safe timestamp extraction
  const getTimestamp = () => {
    return signOff.confirmedAt || signOff.timestamp || signOff.signedAt;
  };

  return (
    <div className="pdf-card" style={{ marginTop: '8px', padding: '10px', backgroundColor: '#f0fdf4', borderRadius: '8px', border: '1px solid #86efac' }}>
      <p style={{ fontSize: '13px', fontWeight: '600', color: '#166534', marginBottom: '8px' }}>Voyage Sign-Off</p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '12px' }}>
        <div>
          <p style={{ color: '#6b7280', fontSize: '10px', textTransform: 'uppercase', marginBottom: '2px' }}>Signed By</p>
          <p style={{ fontWeight: '500' }}>{getSignedByName()}{getRole() ? ` (${getRole()})` : ''}</p>
        </div>
        <div>
          <p style={{ color: '#6b7280', fontSize: '10px', textTransform: 'uppercase', marginBottom: '2px' }}>Date/Time</p>
          <p style={{ fontWeight: '500' }}>{formatDateTime(getTimestamp())}</p>
        </div>
      </div>
      {signOff.notes && (
        <p style={{ fontSize: '11px', color: '#374151', marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #bbf7d0' }}>{signOff.notes}</p>
      )}
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const VoyagePDFContent = ({ voyage, vesselName = 'Unknown Vessel' }) => {
  const unifiedLog = voyage.unifiedLog || [];
  const processedEntries = processUnifiedLog(unifiedLog, voyage);

  // Check if we have pre-departure in processedEntries
  const hasPDCard = processedEntries.some(e => e.type === 'pre_departure_card');

  return (
    <div
      id="voyage-pdf-content"
      style={{
        width: '210mm',
        minHeight: '297mm',
        padding: '15mm',
        backgroundColor: '#f1f5f9',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        fontSize: '12px',
        color: '#1e293b',
        boxSizing: 'border-box'
      }}
    >
      {/* Header */}
      <PDFHeader voyage={voyage} vesselName={vesselName} />

      {/* Voyage Log Entries */}
      <div style={{ marginTop: '12px' }}>
        {processedEntries.map((entry, index) => {
          // Pre-Departure Card
          if (entry.type === 'pre_departure_card') {
            return <PDFPreDepartureCard key={entry.id} voyage={voyage} checks={entry.checks} />;
          }

          // Milestone Banner
          if (entry.type === 'milestone') {
            return <PDFMilestoneBanner key={entry.id || index} entry={entry} />;
          }

          // Weather Report
          if (entry.type === 'weather') {
            return <PDFWeatherCard key={entry.id || index} entry={entry} />;
          }

          // Operation Card
          if (entry.type === 'operation_card') {
            return <PDFOperationCard key={entry.id || `op-${entry.operationId}`} entry={entry} />;
          }

          // Simple entries (note, position, fuel, comms, pilot)
          if (['note', 'position', 'fuel', 'comms', 'pilot'].includes(entry.type)) {
            return <PDFSimpleEntry key={entry.id || index} entry={entry} />;
          }

          // Skip other types
          return null;
        })}

        {/* Fallback: If no pre-departure card in unifiedLog, render from voyage.preDepartureChecks */}
        {!hasPDCard && voyage.preDepartureChecks?.length > 0 && (
          <PDFPreDepartureCard voyage={voyage} checks={voyage.preDepartureChecks} />
        )}
      </div>

      {/* Sign-Off */}
      <PDFSignOff voyage={voyage} />

      {/* Footer */}
      <div style={{ marginTop: '20px', paddingTop: '12px', borderTop: '1px solid #cbd5e1', textAlign: 'center', fontSize: '10px', color: '#64748b' }}>
        <p>Generated by FleetSkipper | {formatDateTime(new Date())}</p>
      </div>
    </div>
  );
};

export default VoyagePDFContent;
